import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import * as authApi from '../api/auth.api.js'
import { setSessionExpiredHandler } from '../api/client.js'
import type { SessionUser } from '../api/auth.api.js'

interface AuthState {
  /** null = belum login; undefined = masih memeriksa sesi awal. */
  readonly user: SessionUser | null | undefined
  login(username: string, password: string): Promise<void>
  logout(): Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined)

  // Token disimpan di memori, jadi muat-ulang halaman selalu mulai dari sini.
  // Sesi lama tidak dipulihkan otomatis pada tahap ini — refresh token juga di
  // memori. Persistensi sesi lintas muat-ulang menyusul bersama keputusan
  // penyimpanan yang ditinjau keamanan (cookie httpOnly di sisi proxy).
  useEffect(() => {
    setUser(null)
    setSessionExpiredHandler(() => setUser(null))
    return () => setSessionExpiredHandler(null)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setUser(await authApi.login(username, password))
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
  }, [])

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (context === null) throw new Error('useAuth dipakai di luar AuthProvider')
  return context
}
